import { Injectable } from '@angular/core';
import { HttpClient } from './http/http-client';

/**
 * Content block for multi-modal LLM requests
 */
export interface TextContent {
  type: 'text';
  text: string;
}

export interface ImageContent {
  type: 'image';
  source: {
    type: 'base64';
    media_type: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';
    data: string; // base64 encoded image data without data URI prefix
  };
}

export type ContentBlock = TextContent | ImageContent;

/**
 * Request interface for LLM autosuggest API
 */
export interface AutosuggestRequest {
  systemPrompt: string;
  prompt: string | ContentBlock[];
  responseSchema: any;
  maxTokens?: number;
}

/**
 * Response interface from LLM autosuggest API
 */
export interface AutosuggestResponse {
  result: any;
  timestamp: string;
  debug?: {
    model: string;
    inputTokens: number;
    outputTokens: number;
    latencyMs: number;
    retries: number;
  };
}

/**
 * Generic LLM service for AI suggestions across the app.
 *
 * Uses the Tidy autosuggest API endpoint for structured suggestions with schema validation.
 * Supports both text-only and multi-modal (text + image) requests.
 *
 * Usage (text-only):
 * const response = await this.llmService.suggest({
 *   systemPrompt: 'You are a helpful assistant...',
 *   prompt: 'Suggest a subject for this action',
 *   responseSchema: { type: 'object', properties: { suggestion: { type: 'string' } } }
 * });
 *
 * Usage (with image):
 * const response = await this.llmService.suggest({
 *   systemPrompt: 'Verify this is a valid selfie',
 *   prompt: [
 *     { type: 'text', text: 'Is this a real person in a real setting?' },
 *     { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: 'base64string...' } }
 *   ],
 *   responseSchema: { type: 'object', properties: { isValid: { type: 'boolean' } } }
 * });
 */
@Injectable()
export class LlmService {
  private readonly API_URL = 'https://chat-api.tidy.com/api/autosuggest';

  constructor(private httpClient: HttpClient) {}

  /**
   * Get AI suggestions using schema-validated responses
   * @param request AutosuggestRequest with systemPrompt, prompt, and responseSchema
   * @returns Promise with structured result matching the provided schema
   */
  async suggest(request: AutosuggestRequest): Promise<AutosuggestResponse> {
    try {
      // Using the httpClient's post method which handles auth, headers, and errors
      const response = await this.httpClient.post(this.API_URL, request);
      return response;
    } catch (error) {
      console.error('LLM Service Error:', error);
      throw error;
    }
  }
}
